// Legal content data. Each section renders as heading + paragraph blocks.
export const LEGAL_DOCS = {
  privacy: {
    slug: "privacy",
    title: "Privacy Policy",
    effectiveDate: "01 August 2026",
    intro:
      'Welcome to RideMate ("RideMate", "we", "our", or "us"). We are committed to protecting your privacy and handling your personal information responsibly. This Privacy Policy explains how RideMate collects, uses, stores, shares, and safeguards personal information when you access or use the RideMate mobile application, website, and related services (collectively referred to as the "Services").\n\nBy accessing or using the Services, you acknowledge that you have read, understood, and agreed to this Privacy Policy.',
    sections: [
      {
        title: "1. Personal Information",
        paragraphs: [
          "To provide a secure, reliable, and efficient ride-sharing platform, RideMate may process certain personal information during account registration, identity verification, ride booking, payment processing, customer support, and other service-related activities. Such information may include your name, mobile number, email address (where applicable), date of birth, profile photograph, vehicle details, Driving Licence, Vehicle Registration Certificate (RC), ride preferences, trip history, and any other information voluntarily provided while using the Services.",
          "RideMate may also process limited technical information, including device information, operating system, application version, IP address, and diagnostic data, to maintain platform security, improve performance, and enhance the overall user experience.",
        ],
      },
      {
        title: "2. Purpose of Processing Information",
        paragraphs: [
          "Personal information is processed only for legitimate business and operational purposes, including identity verification, driver and vehicle verification, facilitating ride matching, processing bookings and payments, providing customer support, improving navigation accuracy, maintaining platform safety, preventing fraud and misuse, communicating important service-related updates, complying with applicable legal obligations, and improving the Services.",
          "RideMate processes only such information as is reasonably necessary for the provision and operation of the Services.",
        ],
      },
      {
        title: "3. Location Information",
        paragraphs: [
          "RideMate processes location information only when necessary to provide core platform functionality. Location data may be used while the application is in use to display nearby rides, determine pickup and drop-off locations, calculate routes, improve ride matching, and enhance user safety.",
          "During an active trip, and only after the Trip Host has been shown an in-app explanation and has granted background location permission, RideMate may continue collecting the Trip Host's location even while the application is running in the background or the device screen is off. This background location is used solely to share the Trip Host's live position with the Co-Traveller for that trip, is accompanied by a persistent notification indicating that tracking is active, and stops automatically once the trip is completed or cancelled. Background location is never collected outside of an active, confirmed trip.",
          "Location information is not sold and is not used for third-party advertising purposes.",
        ],
      },
      {
        title: "4. Camera and Media Access",
        paragraphs: [
          "Access to the device camera and photo library is requested solely for uploading profile photographs and verification documents, including Driving Licence and Vehicle Registration Certificate (RC), where applicable. These permissions are accessed only with the user's consent and may be managed through the device settings at any time.",
        ],
      },
      {
        title: "5. Payment Processing",
        paragraphs: [
          "RideMate facilitates digital payments through authorised third-party payment service providers, including Razorpay. RideMate does not collect or store sensitive payment credentials such as debit card numbers, credit card details, CVV, internet banking credentials, or UPI PINs.",
          "Payment transactions are securely processed by the respective payment provider in accordance with their applicable privacy and security standards.",
        ],
      },
      {
        title: "6. Disclosure of Information",
        paragraphs: [
          "RideMate does not sell, rent, or commercially distribute users' personal information. Personal information may be disclosed only where reasonably necessary for the operation of the Services, including to verified users involved in a confirmed booking, authorised payment partners, trusted technology service providers, or competent governmental and regulatory authorities where required by applicable law.",
          "Such disclosures are limited to the minimum information necessary for the intended purpose.",
        ],
      },
      {
        title: "7. Information Security",
        paragraphs: [
          "RideMate implements reasonable administrative, technical, and organisational safeguards designed to protect personal information against unauthorised access, alteration, disclosure, misuse, or destruction.",
          "While we strive to protect your information using industry-standard security practices, no method of electronic transmission or storage can be guaranteed to be completely secure.",
        ],
      },
      {
        title: "8. Data Retention",
        paragraphs: [
          "Personal information is retained only for as long as reasonably necessary to provide the Services, comply with legal obligations, resolve disputes, prevent fraudulent activities, enforce our agreements, and maintain platform integrity.",
          "Where retention is no longer required, personal information will be securely deleted, anonymised, or otherwise disposed of in accordance with applicable legal requirements.",
        ],
      },
      {
        title: "9. Account Deletion",
        paragraphs: [
          "Users may permanently delete their RideMate account at any time through the application by navigating to Profile → Settings → Delete Account.",
          "Upon successful deletion, personal information associated with the account will be removed from our active systems, except where continued retention is required by applicable law or is reasonably necessary for fraud prevention, financial record-keeping, dispute resolution, or regulatory compliance.",
        ],
      },
      {
        title: "10. Children's Privacy",
        paragraphs: [
          "The Services are intended exclusively for individuals who are at least eighteen (18) years of age. RideMate does not knowingly collect or process personal information from minors.",
        ],
      },
      {
        title: "11. Changes to this Privacy Policy",
        paragraphs: [
          "RideMate reserves the right to modify or update this Privacy Policy from time to time. Any revisions will become effective upon publication within the application or on the official website.",
          "Continued use of the Services following such publication constitutes acceptance of the updated Privacy Policy.",
        ],
      },
      {
        title: "12. Contact Information",
        paragraphs: [
          "If you have any questions, requests, or concerns regarding this Privacy Policy or the processing of your personal information, please contact us at support@ridemateapp.in.",
        ],
      },
    ],
  },

  terms: {
    slug: "terms",
    title: "Terms of Service",
    effectiveDate: "01 August 2026",
    intro:
      'Welcome to RideMate ("RideMate", "we", "our", or "us"). These Terms of Service ("Terms") govern your access to and use of the RideMate mobile application, website, and related services (collectively referred to as the "Services"). By creating an account, accessing, or using the Services, you acknowledge that you have read, understood, and agreed to be bound by these Terms. If you do not agree with these Terms, you must not use the Services.',
    sections: [
      {
        title: "1. Eligibility",
        paragraphs: [
          "The Services are intended only for individuals who are at least eighteen (18) years of age and legally capable of entering into a binding agreement. By using RideMate, you represent and warrant that the information provided during registration is accurate, complete, and up to date.",
        ],
      },
      {
        title: "2. User Account",
        paragraphs: [
          "You are responsible for maintaining the confidentiality of your account and for all activities carried out using your account. You agree to provide accurate information and to promptly update your account whenever necessary. RideMate reserves the right to suspend or terminate any account found to contain false, misleading, or fraudulent information.",
        ],
      },
      {
        title: "3. Trip Hosts and Co-Travellers",
        paragraphs: [
          "RideMate enables verified Trip Hosts to publish available rides and verified Co-Travellers to request available seats. Trip Hosts are responsible for maintaining valid driving licences, vehicle registration documents, and complying with all applicable traffic laws and regulations. Co-Travellers are expected to provide accurate booking information, arrive at the agreed pickup location on time, and conduct themselves respectfully throughout the journey.",
        ],
      },
      {
        title: "4. Verification",
        paragraphs: [
          "To promote trust and safety within the RideMate community, users may be required to complete identity and document verification. RideMate reserves the right to request additional verification information where reasonably necessary. Submission of verification documents does not constitute a guarantee or certification of any user's conduct, identity, or suitability beyond the verification process performed by RideMate.",
        ],
      },
      {
        title: "5. Bookings and Payments",
        paragraphs: [
          "RideMate provides a technology platform that facilitates ride discovery, booking, and payment between users. Payments are processed through authorised third-party payment service providers. RideMate does not store sensitive payment credentials and is not responsible for failures or delays attributable to third-party payment systems.",
        ],
      },
      {
        title: "6. Acceptable Use",
        paragraphs: ["Users agree not to:"],
        bullets: [
          "Provide false or misleading information.",
          "Upload forged or invalid documents.",
          "Impersonate another individual.",
          "Harass, threaten, or abuse other users.",
          "Use the Services for unlawful, fraudulent, or harmful activities.",
          "Attempt to interfere with the security, integrity, or operation of the Platform.",
        ],
        outro:
          "Violation of these Terms may result in suspension or permanent termination of the account without prior notice.",
      },
      {
        title: "7. Privacy",
        paragraphs: [
          "The collection and processing of personal information are governed by the RideMate Privacy Policy. By using the Services, you acknowledge and agree to the processing of your information as described in the Privacy Policy.",
        ],
      },
      {
        title: "8. Intellectual Property",
        paragraphs: [
          "All intellectual property rights relating to the RideMate Platform, including its name, logo, design, software, content, trademarks, and technology, are owned by or licensed to RideMate and are protected under applicable intellectual property laws. No part of the Platform may be copied, reproduced, modified, distributed, or used without prior written permission from RideMate.",
        ],
      },
      {
        title: "9. Limitation of Liability",
        paragraphs: [
          "RideMate operates solely as a technology platform that enables verified users to connect for ride-sharing purposes. RideMate does not own, operate, manage, or control vehicles, nor does it employ drivers or provide transportation services. To the maximum extent permitted by applicable law, RideMate shall not be liable for disputes, delays, cancellations, accidents, loss, damage, personal injury, or any indirect or consequential damages arising from interactions or journeys between users.",
        ],
      },
      {
        title: "10. Suspension and Termination",
        paragraphs: [
          "RideMate reserves the right to suspend, restrict, or permanently terminate access to the Services where a user violates these Terms, applicable laws, community standards, or engages in fraudulent or unsafe activities.",
        ],
      },
      {
        title: "11. Changes to the Services and Terms",
        paragraphs: [
          "RideMate may modify, update, suspend, or discontinue any part of the Services or these Terms from time to time. Updated Terms will become effective upon publication within the application or on the official website. Continued use of the Services after such publication constitutes acceptance of the revised Terms.",
        ],
      },
      {
        title: "12. Governing Law",
        paragraphs: [
          "These Terms shall be governed by and construed in accordance with the laws of India. Any dispute arising out of or relating to these Terms or the Services shall be subject to the exclusive jurisdiction of the competent courts located in India.",
        ],
      },
      {
        title: "13. Contact Us",
        paragraphs: [
          "For any questions regarding these Terms of Service, please contact us at support@ridemateapp.in.",
        ],
      },
    ],
  },

  safety: {
    slug: "safety",
    title: "Safety Policy",
    effectiveDate: "01 August 2026",
    intro:
      "At RideMate, the safety of our users is a priority. RideMate is designed to connect verified Trip Hosts and Co-Travellers through a trusted platform; however, every user also shares responsibility for maintaining a safe and respectful ride-sharing experience. By using the RideMate Services, you agree to follow this Safety Policy together with our Terms of Service and Privacy Policy.",
    sections: [
      {
        title: "1. User Verification",
        paragraphs: [
          "To improve trust and safety, RideMate may require users to complete identity verification before accessing certain features of the Platform. Trip Hosts may also be required to submit valid Driving Licence and Vehicle Registration Certificate (RC) details for verification. Providing false, misleading, or forged information may result in suspension or permanent termination of the account.",
        ],
      },
      {
        title: "2. Safe Ride Practices",
        paragraphs: ["Trip Hosts are expected to:"],
        bullets: [
          "Hold a valid Driving Licence.",
          "Ensure the vehicle is legally registered and roadworthy.",
          "Follow all applicable traffic laws and safety regulations.",
          "Respect the confirmed pickup and drop-off arrangements.",
        ],
        subGroup: {
          heading: "Co-Travellers are expected to:",
          bullets: [
            "Verify ride details before travelling.",
            "Wear a seat belt where available.",
            "Treat the Trip Host, vehicle, and fellow passengers with respect.",
            "Follow reasonable safety instructions during the journey.",
          ],
        },
      },
      {
        title: "3. Live Location Tracking and Emergency (SOS) Assistance",
        paragraphs: [
          "During an active trip, RideMate provides live location tracking so that the Co-Traveller can see the Trip Host's real-time position on a map, both while the Trip Host is approaching pickup and for the duration of the trip. This tracking requires the Trip Host's permission, is accompanied by a visible in-app and system notification while active, and stops automatically once the trip ends or is cancelled.",
          "RideMate also provides an in-app SOS button, available during an active trip, that users may use to alert emergency contacts or seek help in a safety incident. The SOS feature is a convenience tool and does not replace contacting local emergency services directly, as described in the section below.",
        ],
      },
      {
        title: "4. Emergency Situations",
        paragraphs: [
          "If you are involved in an accident, medical emergency, or any situation requiring immediate assistance, contact your local emergency services without delay. RideMate does not provide emergency response services and should not be relied upon for emergency assistance.",
        ],
      },
      {
        title: "5. Reporting Safety Concerns",
        paragraphs: [
          "Users are encouraged to report unsafe behaviour, fraud, harassment, fake profiles, or any activity that may compromise the safety of the RideMate community. RideMate will review reported incidents and may take appropriate action, including issuing warnings, suspending accounts, permanently removing users, or cooperating with competent authorities where required by law.",
        ],
      },
      {
        title: "6. Prohibited Conduct",
        paragraphs: ["The following conduct is strictly prohibited while using RideMate:"],
        bullets: [
          "Driving under the influence of alcohol or drugs.",
          "Harassment, threats, discrimination, or abusive behaviour.",
          "Carrying illegal goods or engaging in unlawful activities.",
          "Using fake identities or forged documents.",
          "Misrepresenting ride details or vehicle information.",
          "Any conduct that endangers the safety of other users.",
        ],
      },
      {
        title: "7. Limitation of Responsibility",
        paragraphs: [
          "RideMate provides a technology platform that enables users to connect for ride-sharing purposes. RideMate does not own or operate vehicles, employ drivers, or supervise individual journeys. While RideMate implements reasonable verification and safety measures, users remain responsible for exercising their own judgment before and during each ride.",
        ],
      },
      {
        title: "8. Updates to this Safety Policy",
        paragraphs: [
          "RideMate may update this Safety Policy from time to time to reflect changes in the Services, legal requirements, or safety practices. Updated versions become effective upon publication within the application or on the official website.",
        ],
      },
      {
        title: "9. Contact Us",
        paragraphs: [
          "For safety-related concerns or questions regarding this Safety Policy, please contact us at support@ridemateapp.in.",
        ],
      },
    ],
  },

  refunds: {
    slug: "refunds",
    title: "Refund & Cancellation Policy",
    effectiveDate: "01 August 2026",
    intro:
      "This Refund & Cancellation Policy explains the terms governing ride cancellations, refunds, and payment-related matters when using the RideMate platform. By booking or offering a ride through RideMate, you agree to this Policy in addition to our Terms of Service.",
    sections: [
      {
        title: "1. Ride Cancellation",
        paragraphs: [
          "Trip Hosts and Co-Travellers may cancel a ride or booking in accordance with the features available on the RideMate Platform. Users are expected to cancel as early as possible to minimise inconvenience to other users. Repeated or intentional cancellations may result in warnings, temporary restrictions, or suspension of the user account.",
        ],
      },
      {
        title: "2. Refund Eligibility",
        paragraphs: [
          "Where a payment has been successfully completed through RideMate, a refund may be issued in circumstances such as:",
        ],
        bullets: [
          "Duplicate or accidental payment.",
          "Payment successfully debited but booking not confirmed due to a technical issue.",
          "Ride cancellation where a refund is applicable under RideMate's policies.",
          "Any other circumstance determined by RideMate after reviewing the transaction.",
        ],
        outro: "Refund requests are subject to verification and approval by RideMate.",
      },
      {
        title: "3. Non-Refundable Situations",
        paragraphs: ["Refunds may not be available where:"],
        bullets: [
          "A user voluntarily cancels after the applicable cancellation period, where such rules are implemented.",
          "Incorrect booking details were provided by the user.",
          "The dispute relates to matters beyond RideMate's reasonable control.",
          "The payment has already been successfully settled in accordance with the completed booking.",
        ],
      },
      {
        title: "4. Refund Processing",
        paragraphs: [
          "Approved refunds will be processed through the original payment method used for the transaction, wherever possible. The time required for the refunded amount to appear in your account may vary depending on your bank, payment provider, or financial institution.",
        ],
      },
      {
        title: "5. Payment Disputes",
        paragraphs: [
          "If you believe you have been charged incorrectly or experience any payment-related issue, please contact RideMate Support as soon as reasonably possible with the relevant transaction details. RideMate will investigate the matter and take appropriate action where applicable.",
        ],
      },
      {
        title: "6. Trip Host Payout",
        paragraphs: [
          "Where a Co-Traveller has successfully completed a ride and the trip is marked as completed on the RideMate Platform, the applicable payout amount will be released to the Trip Host. Payouts will be credited on the same day to the Trip Host's registered bank account or UPI ID, as provided within the RideMate application, subject to successful payment processing and verification.",
          "RideMate may temporarily withhold or delay a payout where reasonably necessary to investigate suspected fraud, payment disputes, policy violations, technical issues, or where required by applicable law. Trip Hosts are responsible for ensuring that their bank account or UPI details are accurate and up to date. RideMate shall not be responsible for payout delays or failures resulting from incorrect or incomplete payment information provided by the Trip Host.",
        ],
      },
      {
        title: "7. Policy Updates",
        paragraphs: [
          "RideMate reserves the right to amend this Refund & Cancellation Policy at any time. Any updates will become effective upon publication within the application or on the official website.",
        ],
      },
      {
        title: "8. Contact Us",
        paragraphs: [
          "For refund, cancellation, or payment-related queries, please contact us at support@ridemateapp.in.",
        ],
      },
    ],
  },
};
